import { useEffect, useState } from 'react';
import {
    message,
    PageHeader,
    Button,
    Layout,
    Anchor,
    Menu,
    Dropdown,
} from 'antd';
import { FilterOutlined, DownOutlined } from '@ant-design/icons';

import { Helmet } from 'react-helmet';

import SurveysService from './SurveysService';
import ApiError from '../core/ApiError';
import Report from './components/Report';
import AuthService from '../auth/AuthService';
import ReportFilters from './components/ReportFilters';
import styles from './surveyresultspage.module.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DownloadReportModal from './components/DownloadReportModal';
import moment from 'moment';

type RouteParams = { id: string; client: string; tab?: string };

const SurveyResultsPage = () => {
    const { id, client } = useParams() as RouteParams;
    const location = useLocation();
    const navigate = useNavigate();

    const [survey, setSurvey] = useState<any>(null);
    const [surveys, setSurveys] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState<any>({
        from: moment().add(-7, 'days').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD'),
    });
    const [reportModalVisible, setReportModalVisible] = useState(false);

    useEffect(() => {
        const querySurvey = async (uuid: string) => {
            try {
                setLoading(true);
                const survey = await SurveysService.getSurveyByUUID(
                    client,
                    uuid
                );
                setSurvey(survey);
            } catch (err) {
                if (err instanceof ApiError && err.data.code === '404') {
                    navigate('/404');
                } else if (
                    err instanceof ApiError &&
                    err.data.statusCode === 403
                ) {
                    const backPage = `/admin/evals/${client}/surveys`;
                    message.error(err.message);
                    navigate(backPage, { replace: true });
                } else {
                    message.error((err as Error).message);
                }
            }

            setLoading(false);
        };

        const querySurveys = async () => {
            try {
                const { results } = await SurveysService.getSurveys(
                    client || AuthService.getClientSlug(),
                    '',
                    '',
                    1,
                    20
                );
                setSurveys(results);
            } catch (err) {
                message.error((err as Error).message);
            }
        };

        if (location.state) {
            setFilters((f: any) => ({
                ...f,
                ...((location.state as any) || {}),
            }));
            navigate(location.pathname, {}); //remove state from route
        }
        querySurveys();
        querySurvey(id);
    }, [id, location.state, location.pathname, client, navigate]);

    const menu = (
        <Menu>
            {surveys
                .filter((s) => survey !== null && s.id !== survey.id)
                .map((s) => (
                    <Menu.Item key={s.id}>
                        <a href={`../${s.uuid}/results`}>{s.title}</a>
                    </Menu.Item>
                ))}
        </Menu>
    );

    return (
        <>
            <Helmet>
                <title>
                    {survey ? survey.title : 'Resultados de la encuesta'}
                </title>
            </Helmet>
            <Layout>
                <Anchor className={styles.anchor}>
                    <Layout.Sider width={300} className={styles.sider}>
                        <Dropdown overlay={menu} trigger={['click']}>
                            <a
                                className={styles.titleLink}
                                href="/#"
                                onClick={(e) => e.preventDefault()}
                            >
                                {survey ? survey.title : 'Cargando...'}{' '}
                                <DownOutlined />
                            </a>
                        </Dropdown>
                        <img
                            src={SurveysService.getImagePath(
                                client || AuthService.getClientSlug()
                            )}
                            alt="logo"
                            className={styles.logo}
                        />
                        <PageHeader
                            title={
                                <>
                                    <FilterOutlined /> Filtros
                                </>
                            }
                        />
                        <ReportFilters
                            initialFilters={filters}
                            client={client || AuthService.getClientSlug()}
                            surveyId={survey ? survey.id : null}
                            onFiltersChange={setFilters}
                        />
                    </Layout.Sider>
                </Anchor>
                <Layout.Content style={{ padding: '24px' }}>
                    <PageHeader
                        onBack={() =>
                            navigate(`/admin/evals/${client}/surveys`)
                        }
                        title="Resultados"
                        extra={[
                            <Button
                                key={1}
                                type="primary"
                                loading={loading}
                                onClick={() => setReportModalVisible(true)}
                            >
                                Exportar Datos ...
                            </Button>,
                        ]}
                    />
                    {survey && (
                        <Report
                            filters={filters}
                            questions={survey ? survey.questions : null}
                            client={client || AuthService.getClientSlug()}
                            surveyId={survey ? survey.id : null}
                        />
                    )}
                </Layout.Content>
            </Layout>
            {survey && (
                <DownloadReportModal
                    filters={filters}
                    client={client}
                    survey={survey}
                    visible={reportModalVisible}
                    onCancel={() => setReportModalVisible(false)}
                />
            )}
        </>
    );
};

export default SurveyResultsPage;
